import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Keyboard,
  Search,
  Settings,
  Edit3,
  Save,
  X,
  Plus,
  Trash2,
  Copy,
  Download,
  Upload,
  Command,
  Option,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  Info,
  AlertCircle,
  Check,
  MoveRight,
} from 'lucide-react';

// Types
interface KeyboardShortcut {
  id: string;
  keys: string[];
  description: string;
  category: 'navigation' | 'tabs' | 'editing' | 'search' | 'general';
  isEnabled: boolean;
  isCustom: boolean;
  action?: string;
}

interface ShortcutCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  shortcuts: KeyboardShortcut[];
}

// Sample shortcuts
const defaultShortcuts: KeyboardShortcut[] = [
  // Navigation
  { id: 's1', keys: ['⌘', 'K'], description: 'Open command palette', category: 'navigation', isEnabled: true, isCustom: false, action: 'commandPalette' },
  { id: 's2', keys: ['⌘', 'P'], description: 'Quick switch to tab', category: 'navigation', isEnabled: true, isCustom: false, action: 'quickSwitch' },
  { id: 's3', keys: ['⌘', '⇧', 'P'], description: 'Open project search', category: 'navigation', isEnabled: true, isCustom: false, action: 'projectSearch' },
  { id: 's4', keys: ['⌘', 'E'], description: 'Go to sessions', category: 'navigation', isEnabled: true, isCustom: false },
  { id: 's5', keys: ['⌘', 'B'], description: 'Toggle sidebar', category: 'navigation', isEnabled: true, isCustom: false },

  // Tabs
  { id: 's6', keys: ['⌘', 'T'], description: 'New tab', category: 'tabs', isEnabled: true, isCustom: false },
  { id: 's7', keys: ['⌘', 'W'], description: 'Close tab', category: 'tabs', isEnabled: true, isCustom: false },
  { id: 's8', keys: ['⌘', '⇧', 'T'], description: 'Reopen last closed tab', category: 'tabs', isEnabled: true, isCustom: false },
  { id: 's9', keys: ['⌘', '1-9'], description: 'Switch to tab 1-9', category: 'tabs', isEnabled: true, isCustom: false },
  { id: 's10', keys: ['⌘', '⇧', '['], description: 'Previous tab', category: 'tabs', isEnabled: true, isCustom: false },
  { id: 's11', keys: ['⌘', '⇧', ']'], description: 'Next tab', category: 'tabs', isEnabled: true, isCustom: false },

  // Editing
  { id: 's12', keys: ['⌘', 'Z'], description: 'Undo', category: 'editing', isEnabled: true, isCustom: false },
  { id: 's13', keys: ['⌘', '⇧', 'Z'], description: 'Redo', category: 'editing', isEnabled: true, isCustom: false },
  { id: 's14', keys: ['⌘', 'A'], description: 'Select all', category: 'editing', isEnabled: true, isCustom: false },
  { id: 's15', keys: ['⌘', 'D'], description: 'Duplicate line/selection', category: 'editing', isEnabled: true, isCustom: false },
  { id: 's16', keys: ['⌘', '/'], description: 'Toggle comment', category: 'editing', isEnabled: true, isCustom: false },

  // Search
  { id: 's17', keys: ['⌘', 'F'], description: 'Find in current', category: 'search', isEnabled: true, isCustom: false },
  { id: 's18', keys: ['⌘', '⇧', 'F'], description: 'Find in all', category: 'search', isEnabled: true, isCustom: false },
  { id: 's19', keys: ['⌘', 'G'], description: 'Find next', category: 'search', isEnabled: true, isCustom: false },
  { id: 's20', keys: ['⌘', '⇧', 'G'], description: 'Find previous', category: 'search', isEnabled: true, isCustom: false },

  // General
  { id: 's21', keys: ['⌘', ','], description: 'Open settings', category: 'general', isEnabled: true, isCustom: false },
  { id: 's22', keys: ['⌘', 'S'], description: 'Save current', category: 'general', isEnabled: true, isCustom: false },
  { id: 's23', keys: ['⌘', 'H'], description: 'Hide app', category: 'general', isEnabled: true, isCustom: false },
  { id: 's24', keys: ['⌘', 'Q'], description: 'Quit app', category: 'general', isEnabled: true, isCustom: false },
];

export const KeyboardShortcutsPage: React.FC = () => {
  // State
  const [shortcuts, setShortcuts] = useState<KeyboardShortcut[]>(defaultShortcuts);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [editingShortcut, setEditingShortcut] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedKeys, setRecordedKeys] = useState<string[]>([]);
  const [showHints, setShowHints] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);

  // New shortcut form state
  const [newKeys, setNewKeys] = useState<string[]>([]);
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState<KeyboardShortcut['category']>('general');

  // Filter shortcuts
  const filteredShortcuts = useMemo(() => {
    let result = shortcuts;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.description.toLowerCase().includes(query) ||
        s.keys.join(' ').toLowerCase().includes(query)
      );
    }

    if (activeCategory) {
      result = result.filter(s => s.category === activeCategory);
    }

    return result;
  }, [shortcuts, searchQuery, activeCategory]);

  // Group shortcuts by category
  const groupedShortcuts = useMemo(() => {
    const groups: Record<string, KeyboardShortcut[]> = {};
    filteredShortcuts.forEach(shortcut => {
      if (!groups[shortcut.category]) {
        groups[shortcut.category] = [];
      }
      groups[shortcut.category].push(shortcut);
    });
    return groups;
  }, [filteredShortcuts]);

  // Categories
  const categories = [
    { id: 'navigation', name: 'Navigation', icon: <ChevronRight size={16} /> },
    { id: 'tabs', name: 'Tabs', icon: <Keyboard size={16} /> },
    { id: 'editing', name: 'Editing', icon: <Edit3 size={16} /> },
    { id: 'search', name: 'Search', icon: <Search size={16} /> },
    { id: 'general', name: 'General', icon: <Settings size={16} /> },
  ];

  // Get modifier icon
  const getModifierIcon = (key: string) => {
    switch (key) {
      case '⌘': return <Command size={14} />;
      case '⌥': return <Option size={14} />;
      case '⇧': return <ArrowUp size={14} />;
      case '⌃': return <Settings size={14} />;
      default: return null;
    }
  };

  // Format keys for display
  const formatKeys = (keys: string[]) => {
    return keys.map((key, index) => {
      const icon = getModifierIcon(key);
      return (
        <React.Fragment key={index}>
          {icon ? (
            <span className="inline-flex items-center justify-center w-8 h-8 bg-khiimori-bg-elevated/15 rounded-lg">
              {icon}
            </span>
          ) : (
            <span className="inline-flex items-center justify-center min-w-[32px] h-8 px-2 bg-khiimori-bg-elevated/15 rounded-lg text-sm font-medium text-khiimori-text-primary">
              {key}
            </span>
          )}
        </React.Fragment>
      );
    });
  };

  // Toggle shortcut enabled
  const toggleShortcut = (shortcutId: string) => {
    setShortcuts(shortcuts.map(s =>
      s.id === shortcutId ? { ...s, isEnabled: !s.isEnabled } : s
    ));
  };

  // Delete shortcut
  const deleteShortcut = (shortcutId: string) => {
    setShortcuts(shortcuts.filter(s => s.id !== shortcutId));
  };

  // Duplicate shortcut
  const duplicateShortcut = (shortcut: KeyboardShortcut) => {
    const newShortcut: KeyboardShortcut = {
      ...shortcut,
      id: `s${Date.now()}`,
      isCustom: true,
      keys: [...shortcut.keys, "'"],
    };
    setShortcuts([...shortcuts, newShortcut]);
  };

  // Add new shortcut
  const addShortcut = () => {
    if (!newDescription || newKeys.length === 0) return;

    const newShortcut: KeyboardShortcut = {
      id: `s${Date.now()}`,
      keys: newKeys,
      description: newDescription,
      category: newCategory,
      isEnabled: true,
      isCustom: true,
    };

    setShortcuts([...shortcuts, newShortcut]);
    setIsAddingNew(false);
    setNewKeys([]);
    setNewDescription('');
    setNewCategory('general');
  };

  // Record key press
  useEffect(() => {
    if (!isRecording) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const keys: string[] = [];
      if (e.metaKey || e.ctrlKey) keys.push('⌘');
      if (e.altKey) keys.push('⌥');
      if (e.shiftKey) keys.push('⇧');
      if (e.ctrlKey) keys.push('⌃');

      const key = e.key;
      if (!['Meta', 'Alt', 'Shift', 'Control'].includes(key)) {
        keys.push(key.toUpperCase().length === 1 ? key.toUpperCase() : key);
      }

      if (keys.length > 0) {
        setRecordedKeys(keys);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRecording]);

  // Export shortcuts
  const exportShortcuts = () => {
    const data = JSON.stringify(shortcuts, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'keyboard-shortcuts.json';
    a.click();
  };

  // Import shortcuts
  const importShortcuts = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target?.result as string);
        setShortcuts([...shortcuts, ...imported]);
      } catch (err) {
        toast.error('Invalid file format');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="h-full flex flex-col bg-khiimori-bg-elevated/10">
      {/* Header */}
      <div className="border-b border-khiimori-border/20 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-khiimori-text-primary">Keyboard Shortcuts</h1>
            <p className="text-sm text-khiimori-text-muted mt-1">Master your workflow with keyboard shortcuts</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowHints(!showHints)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showHints ? 'bg-purple-600 text-white' : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/15'
              }`}
            >
              <Info size={16} />
              Show Hints
            </button>
            <button
              onClick={exportShortcuts}
              className="px-4 py-2 bg-khiimori-bg-elevated/10 text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-elevated/15 transition-colors flex items-center gap-2"
            >
              <Download size={16} />
              Export
            </button>
            <label className="px-4 py-2 bg-khiimori-bg-elevated/10 text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-elevated/15 transition-colors flex items-center gap-2 cursor-pointer">
              <Upload size={16} />
              Import
              <input type="file" accept=".json" onChange={importShortcuts} className="hidden" />
            </label>
            <button
              onClick={() => setIsAddingNew(true)}
              className="px-4 py-2 bg-purple-600 text-khiimori-text-primary rounded-lg hover:bg-purple-500 transition-colors flex items-center gap-2"
            >
              <Plus size={16} />
              Add Shortcut
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search shortcuts..."
              className="w-full pl-10 pr-4 py-2 bg-khiimori-bg-elevated/10 border border-khiimori-border/20 rounded-lg text-khiimori-text-primary placeholder-khiimori-text-muted focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {/* Category filters */}
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              activeCategory === null ? 'bg-purple-600 text-white' : 'bg-khiimori-bg-elevated/10 text-khiimori-text-muted hover:bg-khiimori-bg-elevated/15'
            }`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${
                activeCategory === cat.id ? 'bg-purple-600 text-white' : 'bg-khiimori-bg-elevated/10 text-khiimori-text-muted hover:bg-khiimori-bg-elevated/15'
              }`}
            >
              {cat.icon}
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key="shortcuts"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
              <div key={category} className="bg-khiimori-bg-elevated/10 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-khiimori-border/20 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-purple-400">
                      {categories.find(c => c.id === category)?.icon}
                    </span>
                    <h3 className="font-semibold text-khiimori-text-primary capitalize">{category}</h3>
                  </div>
                  <span className="text-sm text-khiimori-text-muted">{categoryShortcuts.length} shortcuts</span>
                </div>
                <div className="divide-y divide-khiimori-border/20">
                  {categoryShortcuts.map(shortcut => (
                    <div
                      key={shortcut.id}
                      className={`p-4 flex items-center justify-between hover:bg-khiimori-bg-elevated/20 transition-colors ${
                        !shortcut.isEnabled ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          {formatKeys(shortcut.keys)}
                        </div>
                        <div>
                          <p className="text-khiimori-text-primary">{shortcut.description}</p>
                          {shortcut.isCustom && (
                            <span className="text-xs text-purple-400">Custom</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleShortcut(shortcut.id)}
                          className="p-2 rounded-lg transition-colors"
                        >
                          {shortcut.isEnabled ? (
                            <ToggleRight size={20} className="text-purple-400" />
                          ) : (
                            <ToggleLeft size={20} className="text-khiimori-text-muted" />
                          )}
                        </button>
                        <button
                          onClick={() => duplicateShortcut(shortcut)}
                          className="p-2 hover:bg-khiimori-bg-elevated/15 rounded-lg transition-colors"
                        >
                          <Copy size={16} className="text-khiimori-text-muted" />
                        </button>
                        {shortcut.isCustom && (
                          <button
                            onClick={() => deleteShortcut(shortcut.id)}
                            className="p-2 hover:bg-khiimori-bg-elevated/15 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {filteredShortcuts.length === 0 && (
          <div className="text-center py-12">
            <Keyboard size={48} className="mx-auto text-khiimori-text-muted mb-4" />
            <h3 className="text-lg font-medium text-khiimori-text-secondary">No shortcuts found</h3>
            <p className="text-khiimori-text-muted mt-2">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Add shortcut modal */}
      <AnimatePresence>
        {isAddingNew && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 modal-backdrop flex items-center justify-center z-50 p-6"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-khiimori-bg-elevated/10 rounded-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-6 border-b border-khiimori-border/20">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-khiimori-text-primary">Add Custom Shortcut</h2>
                  <button
                    onClick={() => { setIsAddingNew(false); setNewKeys([]); setNewDescription(''); }}
                    className="p-2 hover:bg-khiimori-bg-elevated/15 rounded-lg transition-colors"
                  >
                    <X size={18} className="text-khiimori-text-muted" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* Key recording */}
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Keys</label>
                  <div
                    className={`p-4 bg-khiimori-bg-elevated/10 rounded-lg border-2 border-dashed text-center cursor-pointer transition-colors ${
                      isRecording ? 'border-purple-500' : 'border-khiimori-border/20 hover:border-khiimori-border/30'
                    }`}
                    onClick={() => setIsRecording(!isRecording)}
                  >
                    {newKeys.length > 0 ? (
                      <div className="flex items-center justify-center gap-2">
                        {formatKeys(newKeys)}
                      </div>
                    ) : (
                      <p className="text-khiimori-text-muted">Click to record keys</p>
                    )}
                  </div>
                  <p className="text-xs text-khiimori-text-muted mt-2">Press the key combination you want to use</p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Description</label>
                  <input
                    type="text"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="e.g., Open settings"
                    className="w-full px-4 py-2 bg-khiimori-bg-elevated/15 border border-khiimori-border/30 rounded-lg text-khiimori-text-primary placeholder-khiimori-text-muted focus:outline-none focus:border-purple-500"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Category</label>
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value as KeyboardShortcut['category'])}
                    className="w-full px-4 py-2 bg-khiimori-bg-elevated/15 border border-khiimori-border/30 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-purple-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-khiimori-border/20 flex justify-end gap-3">
                <button
                  onClick={() => { setIsAddingNew(false); setNewKeys([]); setNewDescription(''); }}
                  className="px-4 py-2 bg-khiimori-bg-elevated/15 text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-elevated/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={addShortcut}
                  disabled={newKeys.length === 0 || !newDescription}
                  className="px-4 py-2 bg-purple-600 text-khiimori-text-primary rounded-lg hover:bg-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Shortcut
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hint overlay */}
      {showHints && (
        <div className="fixed bottom-4 right-4 bg-khiimori-bg-elevated/10 rounded-xl p-4 max-w-sm shadow-xl border border-khiimori-border/20">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Keyboard size={18} className="text-purple-400" />
            </div>
            <div>
              <h4 className="font-medium text-khiimori-text-primary">Pro Tip</h4>
              <p className="text-sm text-khiimori-text-muted mt-1">
                Press <span className="inline-flex items-center gap-1 mx-1">
                  <Command size={12} />K
                </span> anywhere to open the command palette
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};